import { csvRow } from "@/lib/csv";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Bell, Check, Download, Search, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

type Notification = {
  id: string;
  email: string;
  product_id: number;
  notified: boolean;
  created_at: string;
  product_name?: string;
};

const PAGE_SIZE = 25;

const AdminNotifications = () => {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState<"all" | "pending" | "notified">("all");
  const queryClient = useQueryClient();

  const { data: notifications = [], isLoading } = useQuery({
    queryKey: ["admin-notifications", search, filter],
    queryFn: async () => {
      let query = supabase
        .from("back_in_stock_notifications")
        .select("*, products(name)")
        .order("created_at", { ascending: false });

      if (search) {
        query = query.ilike("email", `%${search}%`);
      }
      if (filter === "pending") query = query.eq("notified", false);
      if (filter === "notified") query = query.eq("notified", true);

      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []).map((n: any) => ({
        ...n,
        product_name: n.products?.name ?? `Product #${n.product_id}`,
      })) as Notification[];
    },
  });

  const handleMarkNotified = async (id: string) => {
    const { error } = await supabase
      .from("back_in_stock_notifications")
      .update({ notified: true })
      .eq("id", id);
    if (error) toast.error("Failed to update");
    else {
      toast.success("Marked as notified");
      queryClient.invalidateQueries({ queryKey: ["admin-notifications"] });
    }
  };

  const handleMarkAllNotified = async (productId: number) => {
    const { error } = await supabase
      .from("back_in_stock_notifications")
      .update({ notified: true })
      .eq("product_id", productId)
      .eq("notified", false);
    if (error) toast.error("Failed to update");
    else {
      toast.success("All pending marked as notified for this product");
      queryClient.invalidateQueries({ queryKey: ["admin-notifications"] });
    }
  };

  const handleDelete = async (id: string, email: string) => {
    if (!confirm(`Remove ${email} notification?`)) return;
    const { error } = await supabase
      .from("back_in_stock_notifications")
      .delete()
      .eq("id", id);
    if (error) toast.error("Failed to delete");
    else {
      toast.success("Removed");
      queryClient.invalidateQueries({ queryKey: ["admin-notifications"] });
    }
  };

  const exportCSV = () => {
    if (!notifications.length) {
      toast.error("No data to export");
      return;
    }
    const header = "Email,Product,Notified,Date";
    const rows = notifications.map(
      (n) =>
        csvRow([n.email, n.product_name, n.notified ? "Yes" : "No", new Date(n.created_at).toLocaleDateString()])
    );
    const csv = [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `back-in-stock-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${notifications.length} records`);
  };

  const pendingCount = notifications.filter((n) => !n.notified).length;
  const totalPages = Math.max(1, Math.ceil(notifications.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const paged = notifications.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold">Back in Stock Notifications</h1>
          <p className="text-sm text-muted-foreground font-body">
            {notifications.length} total · {pendingCount} pending
          </p>
        </div>
        <Button onClick={exportCSV} variant="outline" size="sm" className="gap-2">
          <Download className="w-4 h-4" />
          Export CSV
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by email…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            className="pl-10"
          />
        </div>
        <div className="flex gap-1">
          {(["all", "pending", "notified"] as const).map((f) => (
            <Button
              key={f}
              size="sm"
              variant={filter === f ? "default" : "ghost"}
              onClick={() => { setFilter(f); setPage(0); }}
              className="capitalize"
            >
              {f}
            </Button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-12 bg-secondary animate-pulse rounded" />
          ))}
        </div>
      ) : notifications.length === 0 ? (
        <div className="text-center py-16">
          <Bell className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
          <p className="text-muted-foreground font-body">No notifications yet</p>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <table className="w-full text-sm font-body">
            <thead>
              <tr className="border-b border-border bg-secondary/50">
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Email</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Product</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Status</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Date</th>
                <th className="w-24"></th>
              </tr>
            </thead>
            <tbody>
              {paged.map((n) => (
                <tr key={n.id} className="border-b border-border/50 last:border-0 hover:bg-secondary/30 transition-colors">
                  <td className="px-4 py-3">{n.email}</td>
                  <td className="px-4 py-3 max-w-[200px] truncate">{n.product_name}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium ${
                      n.notified
                        ? "bg-green-100 text-green-700"
                        : "bg-amber-100 text-amber-700"
                    }`}>
                      {n.notified ? "Notified" : "Pending"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {new Date(n.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                  </td>
                  <td className="px-4 py-3 flex items-center gap-1">
                    {!n.notified && (
                      <button
                        onClick={() => handleMarkNotified(n.id)}
                        className="text-muted-foreground hover:text-green-600 transition-colors"
                        title="Mark as notified"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(n.id, n.email)}
                      className="text-muted-foreground hover:text-destructive transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {notifications.length > PAGE_SIZE && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-border">
              <p className="text-sm text-muted-foreground font-body">
                Page {safePage + 1} of {totalPages}
              </p>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" disabled={safePage === 0} onClick={() => setPage(safePage - 1)}>Prev</Button>
                <Button size="sm" variant="ghost" disabled={safePage >= totalPages - 1} onClick={() => setPage(safePage + 1)}>Next</Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminNotifications;
